/**
 * The sign-in shared across the app's open tabs (docs/decisions/2026-10-21_web-sign-in-shared-across-tabs.md).
 *
 * Each tab keeps the OIDC user in its own sessionStorage, as before, so the browser forgets it when the last tab of the
 * app closes. The open tabs share it over a BroadcastChannel:
 *  - a new tab with no user asks the open tabs, and takes the first answer (adoptFromOpenTab); before the provider
 *    loads the user, a user near expiry or expired (after a sleep) is renewed (prepare);
 *  - a renew runs under a Web Lock, one tab at a time: the tab first asks the others for a fresher user with the same
 *    sub and takes it, and only uses its refresh token when none has one (Zitadel rotates refresh tokens: a used one
 *    stops working). The new user goes to the other tabs, which take it;
 *  - a sign-out goes to every tab: each one removes its user and marks itself "signed out", so it neither asks for nor
 *    sends a user until it completes a new sign-in. A tab that had a user then does what auth.tsx gives
 *    onSignedOutElsewhere (it forgets its pages and goes to the landing, as the tab that signed out does).
 * A browser without BroadcastChannel or Web Locks gets no TabSession (createBrowserTabSession returns null), and the
 * sign-in works as before: plain sessionStorage and oidc-client-ts's own renew.
 */

import { ErrorTimeout, User, type StateStore } from "oidc-client-ts";

/** The channel the app's tabs talk on, and the lock a renew holds. */
export const SESSION_CHANNEL = "sahas-forge-session";
export const RENEW_LOCK = "sahas-forge-session-renew";
/** The mark of a tab that signed out, or was signed out by another tab. */
export const SIGNED_OUT_KEY = "sahas.session.signed-out";
/** How long a tab waits for the other tabs' answers. */
export const ASK_TIMEOUT_MS = 400;
/** The longest a tab waits at start for another tab's user and its renew (prepareWithin). */
export const PREPARE_MAX_MS = 5_000;
/** WebStorageStateStore's prefix: the stored key stays `oidc.user:<authority>:<client_id>`, so open tabs stay signed in. */
const PREFIX = "oidc.";
/** A user is fresh when its access token lives this much longer than the expiring notice. */
const FRESH_SLACK_S = 30;
/** A renew that timed out runs again after this, as oidc-client-ts's own renew does. */
const RETRY_MS = 5_000;
/** The longest a sign-out waits for the refresh token's revoke. */
const REVOKE_TIMEOUT_MS = 3_000;

/** What the tabs send each other. Only the user key's value travels; nothing else in storage does. */
type SessionMessage =
  | { t: "ask"; id: string; key: string }
  | { t: "answer"; id: string; key: string; user: string }
  | { t: "set"; key: string; user: string }
  | { t: "remove"; key: string };

/** The parts of a BroadcastChannel the session uses. */
export interface SessionChannel {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: MessageEvent) => void): void;
  close(): void;
}

/** The parts of navigator.locks the session uses. */
export interface SessionLocks {
  request(name: string, callback: () => Promise<void>): Promise<void>;
}

/** The parts of oidc-client-ts's UserManager the session uses. */
export interface SessionUserManager {
  readonly settings: {
    readonly authority: string;
    readonly client_id: string;
    readonly accessTokenExpiringNotificationTimeInSeconds: number;
  };
  readonly events: {
    load(user: User, raiseEvent?: boolean): Promise<void>;
    unload(): Promise<void>;
    addAccessTokenExpiring(cb: () => void): () => void;
    _raiseSilentRenewError(e: Error): Promise<void>;
  };
  signinSilent(): Promise<User | null>;
  revokeTokens(types?: ("access_token" | "refresh_token")[]): Promise<void>;
}

export interface TabSessionDeps {
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;
  channel: SessionChannel;
  locks: SessionLocks;
  /** Milliseconds since the epoch. */
  now?: () => number;
  askTimeoutMs?: number;
  newId?: () => string;
}

/** A user and the exact string it was stored as. */
interface Stored {
  raw: string;
  user: User;
}

function parse(raw: string | null): Stored | null {
  if (!raw) return null;
  try {
    return { raw, user: User.fromStorageString(raw) };
  } catch {
    return null;
  }
}

export class TabSession {
  private readonly storage: TabSessionDeps["storage"];
  private readonly channel: SessionChannel;
  private readonly locks: SessionLocks;
  private readonly now: () => number;
  private readonly askTimeoutMs: number;
  private readonly newId: () => string;
  private um: SessionUserManager | null = null;
  private userKey = "";
  private readonly asks = new Map<string, (raw: string) => void>();
  private renewing: Promise<void> | null = null;
  private adopting: Promise<boolean> | null = null;
  private signedOutElsewhere: (() => void) | null = null;

  /** The userStore for the UserManager: this tab's sessionStorage, and every change sent to the other tabs. */
  readonly store: StateStore;

  constructor(deps: TabSessionDeps) {
    this.storage = deps.storage;
    this.channel = deps.channel;
    this.locks = deps.locks;
    this.now = deps.now ?? (() => Date.now());
    this.askTimeoutMs = deps.askTimeoutMs ?? ASK_TIMEOUT_MS;
    this.newId = deps.newId ?? (() => crypto.randomUUID());
    this.channel.addEventListener("message", (e) => void this.onMessage(e.data as SessionMessage));
    this.store = {
      get: async (key) => this.storage.getItem(PREFIX + key),
      set: async (key, value) => {
        // A tab that signs out, or was signed out, keeps no user: a renew that ends after the sign-out cannot bring it back.
        if (this.signedOut()) return;
        this.storage.setItem(PREFIX + key, value);
        this.post({ t: "set", key, user: value });
      },
      remove: async (key) => {
        const item = this.storage.getItem(PREFIX + key);
        this.storage.removeItem(PREFIX + key);
        this.post({ t: "remove", key });
        return item;
      },
      getAllKeys: async () => {
        const keys: string[] = [];
        for (let i = 0; i < this.storage.length; i++) {
          const k = this.storage.key(i);
          if (k?.startsWith(PREFIX)) keys.push(k.slice(PREFIX.length));
        }
        return keys;
      },
    };
  }

  /** Connects the UserManager made with this.store: its user key, and the renew when the access token is near expiry. */
  attach(um: SessionUserManager): void {
    this.um = um;
    this.userKey = `user:${um.settings.authority}:${um.settings.client_id}`;
    um.events.addAccessTokenExpiring(() => void this.renew());
  }

  signedOut(): boolean {
    return this.storage.getItem(SIGNED_OUT_KEY) === "1";
  }

  /** A new sign-in in this tab (the OIDC callback) ends the "signed out" mark. */
  clearSignedOut(): void {
    this.storage.removeItem(SIGNED_OUT_KEY);
  }

  /** Whether a new tab asks the open tabs: it has no user and did not sign out. */
  shouldAsk(): boolean {
    // No stored user: null from a browser's Storage, undefined from oidc-client-ts's InMemoryWebStorage.
    return !this.signedOut() && !this.storage.getItem(PREFIX + this.userKey);
  }

  /** At start: takes the user of an open tab, if one answers in time. True when it did. */
  adoptFromOpenTab(): Promise<boolean> {
    this.adopting ??= (async () => {
      if (!this.shouldAsk()) return false;
      const got = await this.ask(null);
      // The tab signed in or out while it waited: keep that.
      if (!got || !this.shouldAsk()) return false;
      this.storage.setItem(PREFIX + this.userKey, got.raw);
      return true;
    })();
    return this.adopting;
  }

  /**
   * Whether the tab has work before the provider loads the user (prepare): no user to ask for, or one near expiry or
   * expired. oidc-client-ts starts no renew for a user that is already expired when it loads, and react-oidc-context
   * shows it signed out: after a laptop sleep, a tab would ask for the password again.
   */
  needsPrepare(): boolean {
    return this.shouldAsk() || this.isDue();
  }

  /** At start: takes an open tab's user if this tab has none, then renews it if it is near expiry. */
  async prepare(): Promise<void> {
    await this.adoptFromOpenTab();
    await this.renewIfDue();
  }

  /** prepare(), but the tab opens after maxMs at the latest: a stuck token call does not keep it on "Loading session…". */
  async prepareWithin(maxMs: number = PREPARE_MAX_MS): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        this.prepare(),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, maxMs);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * The sign-out of this tab and every other: beforeSignOut first, so this tab sends no user any more and the refresh
   * token is revoked, then the redirect (UserManager.signoutRedirect removes the user, which every tab hears).
   */
  async signOut(redirect: () => Promise<void>): Promise<void> {
    await this.beforeSignOut();
    await redirect();
  }

  /** On focus or when the tab shows again: a renew when the access token is near expiry (a hidden tab's timer is late). */
  renewIfDue(): Promise<void> {
    return this.isDue() ? this.renew() : Promise.resolve();
  }

  /** What a tab does when another tab signs out and this one had a user (auth.tsx: forget the pages, go to the landing). */
  onSignedOutElsewhere(cb: () => void): void {
    this.signedOutElsewhere = cb;
  }

  /** A renew, one tab at a time across the app's tabs. */
  renew(): Promise<void> {
    this.renewing ??= this.locks.request(RENEW_LOCK, () => this.renewLocked()).finally(() => {
      this.renewing = null;
    });
    return this.renewing;
  }

  /**
   * Before the sign-out redirect: marks this tab signed out, so it sends no user any more, and revokes the refresh token
   * so that no tab's copy can renew. A revoke that fails or takes too long does not stop the sign-out.
   */
  async beforeSignOut(): Promise<void> {
    this.storage.setItem(SIGNED_OUT_KEY, "1");
    if (!this.um) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        this.um.revokeTokens(["refresh_token"]),
        new Promise<void>((_, reject) => {
          timer = setTimeout(() => reject(new Error("the revoke took too long")), REVOKE_TIMEOUT_MS);
        }),
      ]);
    } catch (err) {
      console.warn("[auth] the refresh token was not revoked at sign-out:", err);
    } finally {
      clearTimeout(timer);
    }
  }

  close(): void {
    this.channel.close();
  }

  private async renewLocked(): Promise<void> {
    const um = this.um;
    if (!um || this.signedOut()) return;
    const local = this.local();
    if (!local) return;
    const fresh = this.freshAfterS();
    // Another tab renewed while this one waited for the lock, and its user already came here.
    if (this.expiresIn(local.user) > fresh) return;
    const peer = await this.ask(local.user.profile.sub);
    // While it asked, the renewed user can also have come as a "set", or a sign-out.
    const current = this.local();
    if (!current || this.signedOut() || this.expiresIn(current.user) > fresh) return;
    if (peer && this.expiresIn(peer.user) > fresh && (peer.user.expires_at ?? 0) > (current.user.expires_at ?? 0)) {
      await this.adopt(peer);
      return;
    }
    try {
      await um.signinSilent();
    } catch (err) {
      if (err instanceof ErrorTimeout) {
        setTimeout(() => void this.renew(), RETRY_MS);
        return;
      }
      await um.events._raiseSilentRenewError(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    // Signed out while the renew ran: the store kept nothing, so the tab shows signed out too.
    if (this.signedOut()) await um.events.unload();
  }

  /** Asks the other tabs; resolves with the first fresh answer, else with the freshest after the timeout. */
  private ask(sub: string | null): Promise<Stored | null> {
    const id = this.newId();
    const fresh = this.freshAfterS();
    return new Promise((resolve) => {
      let best: Stored | null = null;
      const done = () => {
        clearTimeout(timer);
        this.asks.delete(id);
        resolve(best);
      };
      const timer = setTimeout(done, this.askTimeoutMs);
      this.asks.set(id, (raw) => {
        const got = parse(raw);
        if (!got || (sub !== null && got.user.profile.sub !== sub)) return;
        if (!best || (got.user.expires_at ?? 0) > (best.user.expires_at ?? 0)) best = got;
        if (this.expiresIn(best.user) > fresh) done();
      });
      this.post({ t: "ask", id, key: this.userKey });
    });
  }

  private async onMessage(m: SessionMessage): Promise<void> {
    if (!m || typeof m !== "object" || m.key !== this.userKey || !this.userKey) return;
    switch (m.t) {
      case "ask": {
        if (this.signedOut()) return;
        const raw = this.storage.getItem(PREFIX + this.userKey);
        if (raw) this.post({ t: "answer", id: m.id, key: m.key, user: raw });
        return;
      }
      case "answer":
        this.asks.get(m.id)?.(m.user);
        return;
      case "set": {
        // Only a signed-in tab takes a renewed user, of the same person, and only a newer one.
        if (this.signedOut()) return;
        const local = this.local();
        const got = parse(m.user);
        if (!local || !got || got.user.profile.sub !== local.user.profile.sub) return;
        if ((got.user.expires_at ?? 0) <= (local.user.expires_at ?? 0)) return;
        await this.adopt(got);
        return;
      }
      case "remove": {
        this.storage.setItem(SIGNED_OUT_KEY, "1");
        if (!this.storage.getItem(PREFIX + this.userKey)) return;
        this.storage.removeItem(PREFIX + this.userKey);
        await this.um?.events.unload();
        this.signedOutElsewhere?.();
        return;
      }
    }
  }

  /** Stores another tab's user here, not sent on again, and hands it to the UserManager (react-oidc-context sees it). */
  private async adopt(got: Stored): Promise<void> {
    this.storage.setItem(PREFIX + this.userKey, got.raw);
    await this.um?.events.load(got.user);
  }

  private local(): Stored | null {
    return parse(this.storage.getItem(PREFIX + this.userKey));
  }

  private isDue(): boolean {
    const local = this.local();
    return !this.signedOut() && local !== null && this.expiresIn(local.user) <= this.freshAfterS();
  }

  private expiresIn(user: User): number {
    return (user.expires_at ?? 0) - this.now() / 1000;
  }

  private freshAfterS(): number {
    return (this.um?.settings.accessTokenExpiringNotificationTimeInSeconds ?? 60) + FRESH_SLACK_S;
  }

  private post(m: SessionMessage): void {
    this.channel.postMessage(m);
  }
}

/**
 * The browser's TabSession, or null where the browser lacks BroadcastChannel or Web Locks (the sign-in then works as
 * before). A tab that shows again, or gets the focus, renews when its access token is near expiry.
 */
export function createBrowserTabSession(): TabSession | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  if (typeof navigator === "undefined" || !navigator.locks) return null;
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    return null;
  }
  const locks = navigator.locks;
  const session = new TabSession({
    storage,
    channel: new BroadcastChannel(SESSION_CHANNEL),
    locks: {
      request: async (name, callback) => {
        await locks.request(name, callback);
      },
    },
  });
  const onShow = () => {
    if (document.visibilityState === "visible") void session.renewIfDue();
  };
  document.addEventListener("visibilitychange", onShow);
  window.addEventListener("focus", onShow);
  return session;
}
